import { formatMarginRequirement } from '@/helpers/ohoHelper';
import { useAppSelector } from '@/state/hooks';
import { selectAccountType } from '@/state/reducers/authSlice';
import { PairMap } from '@/state/reducers/marketPairSlice';
import { InstrumentConfig, selectInstrumentConfigs } from '@/state/reducers/ohoSlice';
import { InstrumentResponse } from '@/types/sso';
import { useClickOutside } from '@/utils/hooks/useClickOutside';
import { useCurrency } from '@/utils/hooks/useCurrency';
import { useDisclosure } from '@/utils/hooks/useDisclosure';
import { getInstrumentConfig } from '@/utils/hooks/useInstrument';
import useLanguage from '@/utils/hooks/useLanguage';
import { translateLangType } from '@/utils/providers/OhoProvider';
import { useSso } from '@/utils/providers/SSOProvider';
import { autoUpdate, flip, offset, Placement, size, useFloating } from '@floating-ui/react';
import cn from 'classnames';
import { Fragment, useCallback, useEffect, useMemo, useState } from 'react';
import { useDateFormatter, useNumberFormatter } from 'react-aria';
import { MdInfoOutline } from 'react-icons/md';
import Drawer from './Drawer';
import TabFilters from './TabFilters';

export const formConfig = {
    offsetOverride: { mainAxis: 2, crossAxis: -151 }
};

export enum InstrumentInfoTab {
    'General' = 0,
    'Trade Info' = 1
}

export const useInstrumentInfoTabs = (): { label: string; value: InstrumentInfoTab }[] => {
    const lang = useLanguage();
    return [
        { label: lang.common.general, value: InstrumentInfoTab.General },
        { label: lang.common['tradeInfo'], value: InstrumentInfoTab['Trade Info'] }
    ];
};

interface InstrumentInfoProps {
    market?: PairMap;
    placement?: Placement;
    width?: string;
    offsetOverride?: { mainAxis: number; crossAxis: number };
    hasBg?: boolean;
}

const InstrumentInfo = ({ market, placement, width, offsetOverride, hasBg }: InstrumentInfoProps) => {
    const accountType = useAppSelector(selectAccountType);
    const instrumentConfigs = useAppSelector(selectInstrumentConfigs);

    const instrumentInfoTabs = useInstrumentInfoTabs();

    const { jwt, instrumentsIncludeRatioApi, entityType } = useSso();
    const { data: instruments } = instrumentsIncludeRatioApi;

    const isXplorSpot = !location.pathname.includes('/trader');

    const showInstrumentInfo = useDisclosure(false);
    const [instrumentInfoTab, setInstrumentInfoTab] = useState(InstrumentInfoTab.General);
    const numberFormatter = useNumberFormatter({ minimumFractionDigits: 2, maximumFractionDigits: 2 });

    const onClickOutside = useCallback(() => {
        showInstrumentInfo[1].close();
    }, [showInstrumentInfo]);

    const clickOutsideRef = useClickOutside(onClickOutside);
    const [ccy1, ccy2] = useMemo(() => market?.celer?.split('/') || [], [market?.celer]);

    const ccy1Config = useCurrency(ccy1);
    const ccy2Config = useCurrency(ccy2);

    const publicInstrumentConfig = useMemo(() => {
        return getInstrumentConfig(accountType, instrumentConfigs, market?.celer);
    }, [market, instrumentConfigs, accountType]);

    const detailedInstrumentConfig = useMemo(() => {
        return instruments?.find((instrument) => {
            return findInstrumentInfo(isXplorSpot, publicInstrumentConfig, instrument, market);
        });
    }, [publicInstrumentConfig, instruments, market]);

    const underlyingCcy = useCurrency(detailedInstrumentConfig?.underlying || '');

    const { isCFD, isRollingFiatFX } = useMemo(
        () => ({
            isCFD: detailedInstrumentConfig?.type === 'CFD',
            isRollingFiatFX: detailedInstrumentConfig?.type === 'FX' && detailedInstrumentConfig?.subType === 'Spot',
            isCryptoSpot: detailedInstrumentConfig?.type === 'Crypto' && detailedInstrumentConfig?.subType === 'Spot'
        }),
        [detailedInstrumentConfig]
    );

    const marginRequirement = useMemo(() => {
        return formatMarginRequirement(detailedInstrumentConfig?.marginRequirement, numberFormatter);
    }, [detailedInstrumentConfig]);

    const middleware = useMemo(() => {
        return [
            size({
                apply({ rects, elements }) {
                    Object.assign(elements.floating.style, { width });
                }
            }),
            offset(offsetOverride || { mainAxis: 0, crossAxis: -2 }),
            flip()
        ];
    }, []);

    const { refs, floatingStyles } = useFloating({
        strategy: 'fixed',
        placement: placement || 'bottom-start',
        whileElementsMounted: autoUpdate,
        middleware
    });

    useEffect(() => {
        if (!jwt || !detailedInstrumentConfig) setInstrumentInfoTab(InstrumentInfoTab['Trade Info']);
        else setInstrumentInfoTab(InstrumentInfoTab.General);
    }, [jwt, detailedInstrumentConfig]);

    return (
        <div ref={clickOutsideRef}>
            <div ref={refs.setReference} onDrag={(e) => e.stopPropagation()} onClick={(e) => e.stopPropagation()}>
                <div
                    title="Instrument Info"
                    className={cn('h-full flex items-center p-1 cursor-pointer', {
                        ['hover:text-brand-primary-light']: !hasBg,
                        ['hover:bg-brand-primary']: hasBg
                    })}
                    onClick={showInstrumentInfo[1].toggle}>
                    <MdInfoOutline className="w-3 h-3" />
                </div>
                {showInstrumentInfo[0] && (
                    <div
                        ref={refs.setFloating}
                        style={floatingStyles}
                        className="flex flex-col gap-2 bg-brand-background border border-brand-neutral-700 z-10 cursor-auto transition-none">
                        <TabFilters.Main>
                            {jwt && detailedInstrumentConfig && (
                                <TabFilters.Filter
                                    className="!text-xs h-[24px]"
                                    selectedFilter={instrumentInfoTab}
                                    setSelectedFilter={setInstrumentInfoTab}
                                    filter={instrumentInfoTabs[0]}
                                />
                            )}
                            <TabFilters.Filter
                                className="!text-xs h-[24px]"
                                selectedFilter={instrumentInfoTab}
                                setSelectedFilter={setInstrumentInfoTab}
                                filter={instrumentInfoTabs[1]}
                            />
                        </TabFilters.Main>
                        {jwt && detailedInstrumentConfig && instrumentInfoTab === InstrumentInfoTab.General && (
                            <div className="flex flex-wrap gap-3 p-2 pt-0 justify-between text-xs">
                                <GeneralInfo
                                    market={market}
                                    detailedInstrumentConfig={detailedInstrumentConfig}
                                    underlyingCcy={underlyingCcy}
                                    isCFD={isCFD}
                                    isRollingFiatFX={isRollingFiatFX}
                                />
                            </div>
                        )}
                        {instrumentInfoTab === InstrumentInfoTab['Trade Info'] && (
                            <div className="flex flex-wrap gap-3 p-2 pt-0 justify-between text-xs">
                                <TradeInfo
                                    publicInstrumentConfig={publicInstrumentConfig}
                                    detailedInstrumentConfig={detailedInstrumentConfig}
                                    marginRequirement={marginRequirement}
                                    entityType={entityType}
                                    ccy1Config={ccy1Config}
                                    ccy2Config={ccy2Config}
                                />
                            </div>
                        )}
                        {/* <Fragment>
                            <Entry label="Type">{publicInstrumentConfig.type}</Entry>
                            <Entry label="Pip Size">{publicInstrumentConfig.pip_size}</Entry>
                            <Entry label="Price Decimals">{publicInstrumentConfig.price_decimals}</Entry>
                            <Entry label="Ccy 2 Enabled">{publicInstrumentConfig.ccy2_enabled}</Entry>
                            <Entry label="Netdania Name">{publicInstrumentConfig.netdania}</Entry>
                            <Entry label="Netdania Provider">{publicInstrumentConfig.provider}</Entry>
                        </Fragment> */}
                    </div>
                )}
            </div>
        </div>
    );
};

export default InstrumentInfo;

export const findInstrumentInfo = (
    isXplorSpot: boolean,
    publicInstrumentConfig: InstrumentConfig,
    instrument: InstrumentResponse,
    market: PairMap | undefined
) => {
    if (isXplorSpot) {
        if ('Future' === publicInstrumentConfig.type) {
            return instrument.tradingSystemSymbol === market?.celer;
        } else if (['Index', 'Commodity'].includes(publicInstrumentConfig.type)) {
            return instrument.name === `CFD/${market?.celer}`;
        } else {
            return instrument.name.replace('CFD/', '') === market?.celer;
        }
    } else {
        if ('Future' === publicInstrumentConfig.type) {
            return instrument.tradingSystemSymbol === market?.celer;
        } else if (['Index', 'Commodity', 'Crypto'].includes(publicInstrumentConfig.type)) {
            return instrument.name === `CFD/${market?.celer}`;
        } else {
            return instrument.name.replace('CFD/', '') === market?.celer;
        }
    }
};

export const GeneralInfo = (props: any) => {
    const lang = useLanguage();
    const { market, detailedInstrumentConfig, underlyingCcy, isCFD, isRollingFiatFX, isContent } = props;
    return (
        <Fragment>
            <Drawer.InfoSubGroup
                className={cn({ '!text-xs': isContent })}
                valueClassName="whitespace-nowrap"
                labelClassName="whitespace-nowrap"
                details={{
                    [lang.common.symbol]: { value: market?.show || '-' },
                    [lang.common.type]: { value: detailedInstrumentConfig.type },
                    [lang.common.underlying]: {
                        value: underlyingCcy.show || detailedInstrumentConfig.underlying,
                        hidden: !isCFD
                    },
                    [lang.common.ccy1]: {
                        value: detailedInstrumentConfig.currency1,
                        hidden: isCFD
                    },
                    CFI: {
                        value: detailedInstrumentConfig.cfi || '-',
                        hidden: !isCFD && !isRollingFiatFX
                    }
                }}
            />
            <Drawer.InfoSubGroup
                className={cn({ 'flex-[3_1_0]': !isContent, '!text-xs flex-[3_1_0]': isContent })}
                valueClassName="whitespace-nowrap"
                labelClassName="whitespace-nowrap"
                details={{
                    [lang.common.description]: {
                        value: detailedInstrumentConfig.description || '-'
                    },
                    [lang.common.subType]: { value: detailedInstrumentConfig.subType },
                    [lang.common.ccy]: {
                        value: detailedInstrumentConfig.currency2,
                        hidden: !isCFD
                    },
                    [lang.common.ccy2]: {
                        value: detailedInstrumentConfig.currency2,
                        hidden: isCFD
                    },
                    UPI: {
                        value: detailedInstrumentConfig.upi || '-',
                        hidden: !isCFD && !isRollingFiatFX
                    },
                    [lang.common.mifidScope]: {
                        value: detailedInstrumentConfig.miFIDscope ? lang.common.enabled : lang.common.disabled,
                        hidden: !isCFD
                    }
                }}
            />
        </Fragment>
    );
};

export const TradeInfo = (props: any) => {
    const lang = useLanguage();
    const dateFormatter = useDateFormatter({ timeZone: 'UTC' });

    const {
        publicInstrumentConfig,
        detailedInstrumentConfig,
        marginRequirement,
        entityType,
        ccy1Config,
        ccy2Config,
        isContent
    } = props;

    const showInstrumentMinOrderSize = useMemo(() => {
        return (
            ['Index', 'Commodity', 'Future'].includes(publicInstrumentConfig.type) ||
            (publicInstrumentConfig.type === 'Crypto' && publicInstrumentConfig.subType === 'Crypto')
        );
    }, [publicInstrumentConfig.type, publicInstrumentConfig.subType]);

    return (
        <Fragment>
            <Drawer.InfoSubGroup
                className={cn({ '!text-xs': isContent })}
                valueClassName="whitespace-nowrap"
                // valueClassName={isContent ? 'whitespace-nowrap' : undefined}
                labelClassName="whitespace-nowrap"
                details={{
                    [lang.common.type]: { value: translateLangType(publicInstrumentConfig.type, lang) },
                    [lang.common.priceDecimals]: { value: publicInstrumentConfig.price_decimals },
                    [lang.accountValue.marginUsed]: {
                        value: marginRequirement || '-',
                        hidden: entityType.isDeliverableClient
                    }
                }}
            />
            <Drawer.InfoSubGroup
                className={cn({ 'flex-[2_1_0]': !isContent, '!text-xs flex-[2_1_0]': isContent })}
                valueClassName="whitespace-nowrap"
                // valueClassName={isContent ? 'whitespace-nowrap' : undefined}
                labelClassName="whitespace-nowrap"
                details={{
                    [lang.common.ccy2Enabled]: {
                        value: publicInstrumentConfig.ccy2_enabled ? lang.common.yes : lang.common.no,
                        hidden: ['Index', 'Commodity', 'Future'].includes(publicInstrumentConfig.type)
                    },
                    [lang.common.minOrderSize]: {
                        value: showInstrumentMinOrderSize
                            ? `${publicInstrumentConfig?.minOrderSize}`
                            : `${ccy1Config.show} ${ccy1Config.min_order_size}`,
                        hidden: !!publicInstrumentConfig.ccy2_enabled
                    },
                    [lang.common.minOrderSizeCcy1]: {
                        value: `${ccy1Config.show} ${ccy1Config.min_order_size}`,
                        hidden: !publicInstrumentConfig.ccy2_enabled
                    },
                    [lang.common.minOrderSizeCcy2]: {
                        value: `${ccy2Config.show} ${ccy2Config.min_order_size}`,
                        hidden: !publicInstrumentConfig.ccy2_enabled
                    },
                    ['First Trade']: {
                        value: detailedInstrumentConfig?.startDate
                            ? dateFormatter.format(new Date(detailedInstrumentConfig.startDate))
                            : '-',
                        hidden:
                            !detailedInstrumentConfig?.startDate || !['Future'].includes(publicInstrumentConfig.type)
                    },
                    ['Last Trade']: {
                        value: detailedInstrumentConfig?.endDate
                            ? dateFormatter.format(new Date(detailedInstrumentConfig.endDate))
                            : '-',
                        hidden: !detailedInstrumentConfig?.endDate || !['Future'].includes(publicInstrumentConfig.type)
                    }
                }}
            />
        </Fragment>
    );
};
